// Currency system configuration and utilities
export const CURRENCY_CONFIG = {
  // Primary currency
  primary: {
    code: 'EGP',
    symbol: 'EGP',
    name: 'Egyptian Pound',
    nameAr: 'جنيه مصري',
    decimalPlaces: 2,
    position: 'after' as const, // 'before' | 'after'
    thousandsSeparator: ',',
    decimalSeparator: '.'
  }
} as const;

// Currency formatting options
export const CURRENCY_FORMAT_OPTIONS = {
  style: 'currency' as const,
  currency: 'EGP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
} as const;

// Currency utility functions
export const formatCurrency = (
  amount: number,
  options: {
    showSymbol?: boolean;
    showCode?: boolean;
    language?: 'en' | 'ar';
    compact?: boolean;
  } = {}
) => {
  const {
    showSymbol = true,
    showCode = false,
    language = 'en',
    compact = false
  } = options;

  // Handle zero and negative amounts
  if (amount === 0) {
    return language === 'ar' ? '0 جنيه' : '0 EGP';
  }

  // Format the number
  let formattedAmount: string;
  
  if (compact && amount >= 1000) {
    // Compact format for large numbers
    const formatter = new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    });
    formattedAmount = formatter.format(amount);
  } else {
    // Standard format
    const formatter = new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    formattedAmount = formatter.format(amount);
  }

  // Add currency symbol/code
  if (showSymbol) {
    if (language === 'ar') {
      return `${formattedAmount} ${CURRENCY_CONFIG.primary.nameAr}`;
    } else {
      return `${formattedAmount} ${CURRENCY_CONFIG.primary.symbol}`;
    }
  } else if (showCode) {
    return `${formattedAmount} ${CURRENCY_CONFIG.primary.code}`;
  }

  return formattedAmount;
};

// Format price with currency
export const formatPrice = (
  price: number,
  originalPrice?: number,
  language: 'en' | 'ar' = 'en'
) => {
  const formattedPrice = formatCurrency(price, { language });
  
  if (originalPrice && originalPrice > price) {
    const formattedOriginal = formatCurrency(originalPrice, { language });
    return {
      current: formattedPrice,
      original: formattedOriginal,
      discount: Math.round(((originalPrice - price) / originalPrice) * 100)
    };
  }
  
  return {
    current: formattedPrice,
    original: null,
    discount: 0
  };
};

// Parse currency string to number
export const parseCurrency = (currencyString: string): number => {
  // Remove currency symbols and codes
  const cleaned = currencyString
    .replace(/[^\d.,]/g, '')
    .replace(/,/g, '');
  
  return parseFloat(cleaned) || 0;
};

// Validate currency amount
export const isValidCurrency = (amount: number): boolean => {
  return !isNaN(amount) && amount >= 0 && isFinite(amount);
};

// Get currency display for different contexts
export const getCurrencyDisplay = (
  context: 'price' | 'total' | 'discount' | 'shipping',
  language: 'en' | 'ar' = 'en'
) => {
  const displays = {
    en: {
      price: 'EGP',
      total: 'Total: EGP',
      discount: 'Save EGP',
      shipping: 'Shipping: EGP'
    },
    ar: {
      price: 'جنيه',
      total: 'المجموع: جنيه',
      discount: 'وفر جنيه',
      shipping: 'الشحن: جنيه'
    }
  };

  return displays[language][context];
};

// Currency conversion utilities (for future use)
export const convertCurrency = (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  exchangeRate: number
): number => {
  if (fromCurrency === toCurrency) {
    return amount;
  }
  
  return amount * exchangeRate;
};

// Price range formatting
export const formatPriceRange = (
  minPrice: number,
  maxPrice: number,
  language: 'en' | 'ar' = 'en'
) => {
  const minFormatted = formatCurrency(minPrice, { language });
  const maxFormatted = formatCurrency(maxPrice, { language });
  
  if (language === 'ar') {
    return `${minFormatted} - ${maxFormatted}`;
  } else {
    return `${minFormatted} - ${maxFormatted}`;
  }
};

// Discount calculation
export const calculateDiscount = (
  originalPrice: number,
  currentPrice: number
): { amount: number; percentage: number } => {
  const discountAmount = originalPrice - currentPrice;
  const discountPercentage = (discountAmount / originalPrice) * 100;
  
  return {
    amount: discountAmount,
    percentage: Math.round(discountPercentage)
  };
};

// Currency input validation
export const validateCurrencyInput = (value: string): boolean => {
  // Allow numbers, decimal points, and commas
  const currencyRegex = /^[\d,]*\.?\d{0,2}$/;
  return currencyRegex.test(value);
};

// Format currency for input fields
export const formatCurrencyInput = (value: string): string => {
  // Remove all non-numeric characters except decimal point
  let cleaned = value.replace(/[^\d.]/g, '');
  
  // Ensure only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Limit decimal places to 2
  if (parts.length === 2 && parts[1].length > 2) {
    cleaned = parts[0] + '.' + parts[1].substring(0, 2);
  }
  
  return cleaned;
};

// Currency constants
export const CURRENCY_CONSTANTS = {
  MIN_PRICE: 0,
  MAX_PRICE: 999999.99,
  DEFAULT_DECIMAL_PLACES: 2,
  THOUSANDS_SEPARATOR: ',',
  DECIMAL_SEPARATOR: '.'
} as const;

// Currency types
export type CurrencyCode = 'EGP';
export type CurrencyPosition = 'before' | 'after';
export type CurrencyLanguage = 'en' | 'ar';

export interface CurrencyFormat {
  code: CurrencyCode;
  symbol: string;
  name: string;
  nameAr: string;
  decimalPlaces: number;
  position: CurrencyPosition;
  thousandsSeparator: string;
  decimalSeparator: string;
}

export interface PriceFormat {
  current: string;
  original: string | null;
  discount: number;
} 